/* ==========================================================================
   CASA DUNO — theme.js
   JavaScript vanilla, sin dependencias. Mejora progresiva: todo el contenido
   y la navegación funcionan sin JS (enlaces nativos, <details>, formularios).
   ========================================================================== */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  /* ---------- Bloqueo de scroll ---------- */
  const scrollLock = {
    count: 0,
    lock() {
      if (this.count++ === 0) {
        const gap = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = gap > 0 ? `${gap}px` : '';
        document.body.classList.add('is-locked');
      }
    },
    unlock() {
      if (this.count > 0 && --this.count === 0) {
        document.body.classList.remove('is-locked');
        document.body.style.paddingRight = '';
      }
    },
  };

  /* ---------- Drawers (menú móvil, búsqueda) ---------- */
  class Drawer {
    constructor(el) {
      this.el = el;
      this.panel = el.querySelector('.drawer__panel');
      this.trigger = null;
      this.isOpen = false;
      this.onKeydown = this.onKeydown.bind(this);
      el.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      el.inert = true;
    }

    open(trigger) {
      if (this.isOpen) return;
      Drawer.closeAll(this);
      this.isOpen = true;
      this.trigger = trigger || document.activeElement;
      this.el.inert = false;
      this.el.setAttribute('aria-hidden', 'false');
      this.el.classList.add('is-open');
      document.querySelectorAll(`[aria-controls="${this.el.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
      document.querySelector('[data-header]')?.classList.add('menu-is-open');
      scrollLock.lock();
      document.addEventListener('keydown', this.onKeydown);

      const target = this.el.querySelector('input[type="search"]') || this.panel;
      // Espera a que el panel sea visible antes de mover el foco
      setTimeout(() => target.focus({ preventScroll: true }), 60);
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      this.el.inert = true;
      document.querySelectorAll(`[aria-controls="${this.el.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (!Drawer.instances.some((d) => d.isOpen)) {
        document.querySelector('[data-header]')?.classList.remove('menu-is-open');
      }
      scrollLock.unlock();
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus && this.trigger && document.contains(this.trigger)) {
        this.trigger.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Trampa de foco dentro del panel
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (n) => n.offsetParent !== null || n === document.activeElement
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    static closeAll(except) {
      Drawer.instances.forEach((d) => d !== except && d.close({ restoreFocus: false }));
    }
  }
  Drawer.instances = [];

  const initDrawers = () => {
    // Elimina instancias cuyo nodo ya no existe (recarga de secciones en el editor)
    Drawer.instances = Drawer.instances.filter((d) => {
      if (document.contains(d.el)) return true;
      if (d.isOpen) scrollLock.unlock();
      return false;
    });
    document.querySelectorAll('[data-drawer]').forEach((el) => {
      if (!Drawer.instances.some((d) => d.el === el)) Drawer.instances.push(new Drawer(el));
    });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = Drawer.instances.find((d) => d.el.id === opener.dataset.drawerOpen);
    if (!drawer) return; // sin drawer, el enlace sigue su href (fallback /search)
    event.preventDefault();
    drawer.isOpen ? drawer.close() : drawer.open(opener);
  });

  // Al pasar a desktop se cierra el menú móvil
  window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => {
    if (e.matches) Drawer.instances.filter((d) => d.el.id === 'MenuDrawer').forEach((d) => d.close({ restoreFocus: false }));
  });

  /* ---------- Header: sticky, scroll, transparencia ---------- */
  let headerCleanup = null;

  const initHeader = () => {
    if (headerCleanup) headerCleanup();
    const header = document.querySelector('[data-header]');
    if (!header) return;

    // La transparencia solo tiene sentido si el primer bloque del main es un hero con overlay
    if (header.classList.contains('header-wrapper--transparent')) {
      const firstSection = document.querySelector('#MainContent > .shopify-section');
      if (!firstSection || !firstSection.querySelector('[data-header-overlay]')) {
        header.classList.remove('header-wrapper--transparent');
        const wrap = header.closest('.shopify-section');
        if (wrap) wrap.style.marginBottom = '0';
      }
    }

    const mode = header.dataset.sticky;
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (mode === 'on-scroll-up') {
        const dropdownOpen = header.querySelector('details[open]');
        const hide = y > lastY && y > header.offsetHeight * 2 && !dropdownOpen && !header.classList.contains('menu-is-open');
        header.classList.toggle('is-hidden', hide);
      }
      lastY = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    // Dropdowns desktop: uno abierto a la vez, cierre con Escape y clic afuera
    const dropdowns = [...header.querySelectorAll('[data-nav-dropdown]')];
    const onToggle = (event) => {
      if (event.target.open) dropdowns.forEach((d) => d !== event.target && (d.open = false));
    };
    const onDocClick = (event) => {
      if (!header.contains(event.target)) dropdowns.forEach((d) => (d.open = false));
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      const open = dropdowns.find((d) => d.open);
      if (open) {
        open.open = false;
        open.querySelector('summary').focus();
      }
    };
    const onFocusOut = (event) => {
      dropdowns.forEach((d) => {
        if (d.open && !d.contains(event.relatedTarget)) d.open = false;
      });
    };
    dropdowns.forEach((d) => {
      d.addEventListener('toggle', onToggle);
      d.addEventListener('focusout', onFocusOut);
    });

    if (mode !== 'none') window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    update();

    headerCleanup = () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  };

  /* ---------- Barra de anuncios rotativa ---------- */
  const rotators = new Map();
  const initAnnouncements = (root = document) => {
    root.querySelectorAll('[data-announcement-rotator]').forEach((el) => {
      if (rotators.has(el)) return;
      const slides = [...el.querySelectorAll('.announcement__slide')];
      if (slides.length < 2) return;
      let index = 0;
      let timer = null;
      const show = (i) => {
        slides.forEach((s, n) => s.setAttribute('aria-hidden', String(n !== i)));
        slides.forEach((s, n) => (s.inert = n !== i));
        index = i;
      };
      const start = () => {
        if (reducedMotion.matches || timer) return;
        timer = setInterval(() => show((index + 1) % slides.length), Number(el.dataset.interval) || 5000);
      };
      const stop = () => {
        clearInterval(timer);
        timer = null;
      };
      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', start);
      el.addEventListener('focusin', stop);
      el.addEventListener('focusout', start);
      show(0);
      start();
      rotators.set(el, { stop, show, slides });
    });
  };

  /* ---------- Revelado al entrar en viewport ---------- */
  let revealObserver = null;
  const initReveal = (root = document) => {
    const targets = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!document.body.classList.contains('motion-enabled') || reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    targets.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Newsletter: mostrar mensaje solo en el formulario enviado ---------- */
  const initForms = () => {
    const hash = window.location.hash.slice(1);
    document.querySelectorAll('.newsletter-form').forEach((form) => {
      const message = form.querySelector('.form-message');
      if (!message) return;
      if (hash && form.id && form.id !== hash) {
        message.hidden = true;
        form.querySelector('[aria-describedby]')?.removeAttribute('aria-describedby');
        form.querySelector('[aria-invalid]')?.removeAttribute('aria-invalid');
      } else if (message.hasAttribute('data-autofocus')) {
        message.focus({ preventScroll: false });
      }
    });
  };

  /* ---------- Inicialización ---------- */
  const init = () => {
    initDrawers();
    initHeader();
    initAnnouncements();
    initReveal();
    initForms();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------- Theme Editor ---------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initDrawers();
      initHeader();
      initAnnouncements(event.target);
      // En el editor se muestra todo sin esperar al scroll
      event.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    });
    document.addEventListener('shopify:section:unload', () => {
      Drawer.instances.forEach((d) => d.close({ restoreFocus: false }));
      requestAnimationFrame(initDrawers);
    });
    document.addEventListener('shopify:section:select', (event) => {
      event.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    });
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('.announcement__slide');
      if (slide) {
        const rotator = rotators.get(slide.parentElement);
        if (rotator) {
          rotator.stop();
          rotator.show(rotator.slides.indexOf(slide));
        }
      }
      event.target.scrollIntoView?.({ block: 'nearest', inline: 'start', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const slide = event.target.closest('.announcement__slide');
      const rotator = slide && rotators.get(slide.parentElement);
      if (rotator) rotator.show(0);
    });
  }
})();
